/* eslint-disable @typescript-eslint/no-unused-expressions */
import { CSSProperties, HTMLAttributes, KeyboardEvent, useEffect, useRef, useState } from 'react';

import MinusIcon from '~/icons/minus.svg';
import PlusIcon from '~/icons/plus.svg';

import styles from './quantityInput.module.css';

type FontSize = '4xs' | '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';

type Props = Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> & {
	value: number;
	onChange: (amount: number) => void;
	size?: FontSize;
};

const fontSizeMap: Record<FontSize, string> = {
	'4xs': 'var(--font-size-4xs)',
	'3xs': 'var(--font-size-3xs)',
	'2xs': 'var(--font-size-2xs)',
	xs: 'var(--font-size-xs)',
	sm: 'var(--font-size-sm)',
	md: 'var(--font-size-md)',
	lg: 'var(--font-size-lg)',
	xl: 'var(--font-size-xl)',
	'2xl': 'var(--font-size-2xl)',
	'3xl': 'var(--font-size-3xl)',
	'4xl': 'var(--font-size-4xl)',
	'5xl': 'var(--font-size-5xl)',
	'6xl': 'var(--font-size-6xl)',
	'7xl': 'var(--font-size-7xl)',
};

export function QuantityInput({ value, onChange, size = 'sm', style, ...rest }: Props) {
	// We debounce blur to allow the user to press + and - while the input
	// field is empty
	const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const [inputValue, setInputValue] = useState<string | number>(value);

	const numericInputValue = parseInt(inputValue + '') || 0;

	// Handle keydown manually to avoid number input field arrows
	const onKeyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
		const input = e.currentTarget;
		const value = parseInt(input.value);

		if (e.key == 'ArrowUp') {
			onChange(value + 1);
			e.preventDefault();
		} else if (e.key == 'ArrowDown') {
			onChange(value - 1);
			e.preventDefault();
		}
	};

	const updateValue = (value: string | number) => {
		blurTimeoutRef.current && clearInterval(blurTimeoutRef.current);
		setInputValue(value);

		if (value !== '') {
			onChange(Math.max(parseInt(value + ''), 0));
		}
	};

	// If the user deletes all values we allow them
	// to add a new number before assuming they wanted to delete
	const onBlurHandler = () => {
		blurTimeoutRef.current = setTimeout(() => {
			onChange(numericInputValue);
		}, 200);
	};

	// This useEffect ensures the correct item data is pushed to basket
	// without this the item can have the wrong stockState (eg. not showing preorder)
	useEffect(() => {
		updateValue(value);
	}, [value]);

	return (
		<div
			{...rest}
			className={styles.root}
			style={{ ...style, '--qi-font-size': fontSizeMap[size] } as CSSProperties}
			onBlur={onBlurHandler}
			onFocus={() => blurTimeoutRef.current && clearTimeout(blurTimeoutRef.current)}
		>
			<div className={styles.content}>
				<button
					className={styles.button}
					disabled={!value}
					onClick={() => updateValue(numericInputValue - 1)}
					aria-label="Minus"
				>
					<MinusIcon />
				</button>
				<input
					className={styles.input}
					value={inputValue}
					inputMode="numeric"
					onKeyDown={onKeyDownHandler}
					onChange={(e) => updateValue(e.currentTarget.value)}
					aria-label="Quantity"
				/>
				<button
					className={styles.button}
					onClick={() => updateValue(numericInputValue + 1)}
					aria-label="Plus"
				>
					<PlusIcon />
				</button>
			</div>
		</div>
	);
}
